import React, { useCallback, useEffect, useRef, useState } from 'react';
import { Link, useParams } from 'react-router-dom';
import toast from 'react-hot-toast';
import { ArrowLeft } from 'lucide-react';
import {
  getJob,
  activeResume,
  applyToJob,
  createDocumentUpload,
  confirmDocumentUpload,
} from '../../lib/jobsApi';
import type { PublicJobDetail, PublicTask, SubmissionType } from '../../lib/jobsApi';
import { ApiError } from '../../lib/api';
import { pickExtractor } from '../../lib/extractText';
import TurnstileWidget from '../../components/TurnstileWidget';

/*
 * Velaigal /jobs/:id/apply — the seeker-gated apply flow. Submission IS the
 * application: a candidate picks which of the job's tasks to attempt, gives
 * each a link or an uploaded document plus an optional note, and posts the
 * whole bundle in one applyToJob() call.
 *
 * Per-task state lives in ONE Record<taskId, TaskSubmission> keyed by task
 * id, initialized once the job loads. Document uploads run inline per task
 * (presign PUT -> raw PUT -> confirm, same shape as Resume.tsx's flow, but
 * no text extraction — these files are stored raw) and track their own busy
 * flag in a parallel Record so one task uploading never disables another's
 * controls.
 *
 * Gate order on load: loading -> load error (incl. 404) -> no active resume
 * (full-page notice, no broken form) -> job closed -> the form itself.
 * already_applied / job_closed returned from the SUBMIT call are different:
 * the candidate's typed content must stay on screen, so those render as a
 * banner above the still-intact form rather than replacing it.
 */

const MAX_BYTES = 10 * 1024 * 1024;

const MIME_BY_KIND: Record<'pdf' | 'docx', string> = {
  pdf: 'application/pdf',
  docx: 'application/vnd.openxmlformats-officedocument.wordprocessingml.document',
};

const SUBMISSION_TYPE_LABELS: Record<SubmissionType, string> = {
  hosted_link: 'Hosted link',
  repo: 'Repository',
  video: 'Video',
  document: 'Document',
};

interface TaskSubmission {
  selected: boolean;
  type: SubmissionType | '';
  url: string;
  uploadId: string | undefined;
  uploadName: string | undefined;
  note: string;
}

const EMPTY_TASK_SUBMISSION: TaskSubmission = {
  selected: false,
  type: '',
  url: '',
  uploadId: undefined,
  uploadName: undefined,
  note: '',
};

function isTaskComplete(state: TaskSubmission): boolean {
  if (!state.selected) return true;
  if (!state.type) return false;
  if (state.type === 'document') return !!state.uploadId;
  return /^https?:\/\//.test(state.url.trim());
}

const LoadingNotice: React.FC = () => (
  <div role="status" className="max-w-3xl mx-auto text-center py-24">
    <span aria-hidden="true" className="velaigal-loader-rule mx-auto w-12 block" />
    <p className="mt-4 text-sm text-ink-tertiary">Loading this job.</p>
  </div>
);

const NotFoundNotice: React.FC = () => (
  <div className="max-w-2xl mx-auto text-center py-16">
    <span className="label-editorial block mb-3">Not found</span>
    <h1 className="font-display text-3xl font-medium text-ink tracking-tight mb-4">
      This job isn&apos;t available.
    </h1>
    <p className="text-base text-ink-secondary leading-relaxed mb-8">
      It may have been unpublished, or the link is out of date.
    </p>
    <Link to="/jobs" className="btn-primary">
      Browse open jobs
    </Link>
  </div>
);

const ErrorNotice: React.FC<{ message: string; onRetry: () => void }> = ({ message, onRetry }) => (
  <div className="max-w-2xl mx-auto text-center py-16">
    <span className="label-editorial block mb-3" style={{ color: 'var(--color-error)' }}>
      Something went wrong
    </span>
    <h1 className="font-display text-3xl font-medium text-ink tracking-tight mb-4">
      Couldn&apos;t load this job.
    </h1>
    <p className="text-base text-ink-secondary leading-relaxed mb-8">{message}</p>
    <button type="button" onClick={onRetry} className="btn-secondary">
      Try again
    </button>
  </div>
);

const NoResumeNotice: React.FC<{ jobTitle: string }> = ({ jobTitle }) => (
  <div className="max-w-2xl mx-auto text-center py-16">
    <span className="label-editorial block mb-3">Resume required</span>
    <h1 className="font-display text-3xl font-medium text-ink tracking-tight mb-4">
      Upload your resume before applying.
    </h1>
    <p className="text-base text-ink-secondary leading-relaxed mb-8">
      We need a resume on file before you can apply to {jobTitle}.
    </p>
    <Link to="/job-seeker/resume" className="btn-primary">
      Upload your resume
    </Link>
  </div>
);

const ClosedNotice: React.FC = () => (
  <div className="max-w-2xl mx-auto text-center py-16">
    <span className="label-editorial block mb-3">Closed</span>
    <h1 className="font-display text-3xl font-medium text-ink tracking-tight mb-4">
      This job has closed.
    </h1>
    <p className="text-base text-ink-secondary leading-relaxed mb-8">
      The team is no longer accepting applications for this role.
    </p>
    <Link to="/jobs" className="btn-primary">
      Browse open jobs
    </Link>
  </div>
);

const SuccessScreen: React.FC = () => (
  <div className="max-w-2xl mx-auto text-center py-16">
    <span className="label-editorial block mb-3" style={{ color: 'var(--color-saffron)' }}>
      Application sent
    </span>
    <h1 className="font-display text-3xl font-medium text-ink tracking-tight mb-4">
      Application sent — the team will review your work.
    </h1>
    <span aria-hidden="true" className="block w-12 h-px bg-saffron mx-auto mb-8" />
    <div className="flex items-center justify-center gap-4 flex-wrap">
      <Link to="/job-seeker/applications" className="btn-primary">
        View your applications
      </Link>
      <Link to="/jobs" className="btn-secondary">
        Browse more jobs
      </Link>
    </div>
  </div>
);

const SubmitBanner: React.FC<{ kind: 'already_applied' | 'job_closed'; message: string }> = ({ kind, message }) => (
  <div
    role="alert"
    className="mb-6 flex items-start gap-2.5 px-4 py-3 border border-warning bg-paper text-sm text-ink rounded"
  >
    <span aria-hidden="true" className="inline-block w-1.5 h-1.5 rounded-full bg-warning flex-shrink-0 mt-1.5" />
    <span>
      {message}{' '}
      {kind === 'already_applied' ? (
        <Link to="/job-seeker/applications" className="velaigal-link font-medium">
          View your applications
        </Link>
      ) : (
        <Link to="/jobs" className="velaigal-link font-medium">
          Browse open jobs
        </Link>
      )}
    </span>
  </div>
);

interface TaskCardProps {
  task: PublicTask;
  index: number;
  state: TaskSubmission;
  uploading: boolean;
  onToggle: () => void;
  onChange: (patch: Partial<TaskSubmission>) => void;
  onFileSelect: (file: File) => void;
}

const TaskCard: React.FC<TaskCardProps> = ({ task, index, state, uploading, onToggle, onChange, onFileSelect }) => {
  const fileInputRef = useRef<HTMLInputElement>(null);

  const handleFileChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0] ?? null;
    e.target.value = ''; // allow re-selecting the same file after a failure
    if (file) onFileSelect(file);
  };

  return (
    <article className="card">
      <label className="flex items-start gap-3 cursor-pointer">
        <input
          type="checkbox"
          checked={state.selected}
          onChange={onToggle}
          className="mt-1 h-4 w-4 flex-shrink-0"
          style={{ accentColor: 'var(--color-ink)' }}
          aria-label={`Submit this task: ${task.title}`}
        />
        <span className="min-w-0">
          <span className="label-editorial block" style={{ color: 'var(--color-saffron)' }}>
            Task {String(index + 1).padStart(2, '0')}
          </span>
          <span className="font-display text-lg font-medium text-ink block mt-1">{task.title}</span>
          <span className="text-sm text-ink-secondary block mt-2 leading-relaxed whitespace-pre-line">
            {task.brief}
          </span>
        </span>
      </label>

      {state.selected && (
        <div className="mt-5 pt-5 border-t border-hairline space-y-4">
          <div className="space-y-2">
            <label htmlFor={`type-${task.id}`} className="block text-sm font-medium text-ink">
              Submission type
            </label>
            <select
              id={`type-${task.id}`}
              className="input"
              value={state.type}
              onChange={(e) =>
                onChange({
                  type: e.target.value as SubmissionType,
                  url: '',
                  uploadId: undefined,
                  uploadName: undefined,
                })
              }
            >
              {task.allowedSubmissionTypes.map((t) => (
                <option key={t} value={t}>
                  {SUBMISSION_TYPE_LABELS[t]}
                </option>
              ))}
            </select>
          </div>

          {state.type && state.type !== 'document' && (
            <div className="space-y-2">
              <label htmlFor={`url-${task.id}`} className="block text-sm font-medium text-ink">
                {SUBMISSION_TYPE_LABELS[state.type]} URL
              </label>
              <input
                id={`url-${task.id}`}
                type="url"
                className="input"
                placeholder="https://"
                value={state.url}
                onChange={(e) => onChange({ url: e.target.value })}
              />
            </div>
          )}

          {state.type === 'document' && (
            <div className="space-y-2">
              <span className="block text-sm font-medium text-ink">Document</span>
              <input
                ref={fileInputRef}
                type="file"
                accept=".pdf,.docx"
                onChange={handleFileChange}
                className="hidden"
                aria-hidden="true"
                tabIndex={-1}
              />
              <button
                type="button"
                className="btn-secondary"
                onClick={() => fileInputRef.current?.click()}
                disabled={uploading}
              >
                {uploading ? (
                  <>
                    <span
                      className="inline-block h-3.5 w-3.5 rounded-full border-2 animate-spin"
                      style={{ borderColor: 'rgba(26, 22, 20, 0.2)', borderTopColor: 'var(--color-ink)' }}
                      aria-hidden="true"
                    />
                    <span>Uploading…</span>
                  </>
                ) : state.uploadName ? (
                  'Replace file'
                ) : (
                  'Choose file'
                )}
              </button>
              {state.uploadName && !uploading && (
                <p className="text-sm text-ink-tertiary">{state.uploadName}</p>
              )}
              <p className="text-xs text-ink-tertiary">PDF or DOCX, up to 10MB.</p>
            </div>
          )}

          <div className="space-y-2">
            <label htmlFor={`note-${task.id}`} className="block text-sm font-medium text-ink">
              Note <span className="text-ink-tertiary font-normal">(optional)</span>
            </label>
            <textarea
              id={`note-${task.id}`}
              className="input resize-none"
              rows={3}
              maxLength={5000}
              value={state.note}
              onChange={(e) => onChange({ note: e.target.value })}
              placeholder="Anything you'd like the reviewer to know…"
            />
            <p className="text-xs text-ink-tertiary">{state.note.length}/5000</p>
          </div>
        </div>
      )}
    </article>
  );
};

const Apply: React.FC = () => {
  const { id } = useParams<{ id: string }>();

  const [loading, setLoading] = useState(true);
  const [loadError, setLoadError] = useState<{ message: string; notFound: boolean } | null>(null);
  const [loadNonce, setLoadNonce] = useState(0);
  const [data, setData] = useState<PublicJobDetail | null>(null);
  const [hasResume, setHasResume] = useState(false);

  const [submissionsState, setSubmissionsState] = useState<Record<string, TaskSubmission>>({});
  const [uploading, setUploading] = useState<Record<string, boolean>>({});
  const [submitting, setSubmitting] = useState(false);
  const [applicationId, setApplicationId] = useState<string | null>(null);
  const [banner, setBanner] = useState<{ kind: 'already_applied' | 'job_closed'; message: string } | null>(null);

  const [turnstileToken, setTurnstileToken] = useState<string | undefined>(undefined);
  const [turnstileNonce, setTurnstileNonce] = useState(0);
  const [turnstileLoadFailed, setTurnstileLoadFailed] = useState(false);
  const turnstileSiteKey = import.meta.env.VITE_TURNSTILE_SITE_KEY as string | undefined;

  // Mount/id/retry-triggered load — cancelled-guarded so a fast unmount (or
  // a stale in-flight request from a previous id) can't setState afterward.
  useEffect(() => {
    if (!id) return;
    let cancelled = false;
    setLoading(true);
    setLoadError(null);
    (async () => {
      try {
        const [jobRes, resumeRes] = await Promise.all([getJob(id), activeResume()]);
        if (cancelled) return;
        setData(jobRes);
        setHasResume(!!resumeRes.resume);
        const initial: Record<string, TaskSubmission> = {};
        for (const task of jobRes.tasks) {
          initial[task.id] = { ...EMPTY_TASK_SUBMISSION };
        }
        setSubmissionsState(initial);
      } catch (err) {
        if (cancelled) return;
        if (err instanceof ApiError) {
          setLoadError({ message: err.message, notFound: err.status === 404 });
        } else {
          setLoadError({ message: 'Check your connection and try again.', notFound: false });
        }
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [id, loadNonce]);

  const toggleTask = useCallback((taskId: string, defaultType: SubmissionType) => {
    setSubmissionsState((prev) => {
      const current = prev[taskId] ?? EMPTY_TASK_SUBMISSION;
      const selected = !current.selected;
      return {
        ...prev,
        [taskId]: selected && !current.type ? { ...current, selected, type: defaultType } : { ...current, selected },
      };
    });
  }, []);

  const updateTask = useCallback((taskId: string, patch: Partial<TaskSubmission>) => {
    setSubmissionsState((prev) => ({ ...prev, [taskId]: { ...(prev[taskId] ?? EMPTY_TASK_SUBMISSION), ...patch } }));
  }, []);

  const handleFileSelect = useCallback(async (taskId: string, file: File) => {
    if (file.size > MAX_BYTES) {
      toast.error('That file is larger than 10MB.');
      return;
    }
    let kind: 'pdf' | 'docx';
    try {
      kind = pickExtractor(file.type, file.name);
    } catch (err) {
      toast.error(err instanceof Error ? err.message : 'Only PDF and DOCX documents are supported.');
      return;
    }
    const mime = MIME_BY_KIND[kind];

    setUploading((prev) => ({ ...prev, [taskId]: true }));
    try {
      const { uploadId, putUrl } = await createDocumentUpload({ filename: file.name, mime, size: file.size });
      // Only the content-type header — the browser sets content-length
      // itself, and the presigned url was signed against this exact mime.
      const putRes = await fetch(putUrl, {
        method: 'PUT',
        headers: { 'content-type': mime },
        body: file,
      });
      if (!putRes.ok) throw new Error('Upload failed.');
      await confirmDocumentUpload(uploadId);
      updateTask(taskId, { uploadId, uploadName: file.name });
    } catch (err) {
      toast.error(err instanceof ApiError ? err.message : 'Could not upload that file. Check your connection and try again.');
    } finally {
      setUploading((prev) => ({ ...prev, [taskId]: false }));
    }
  }, [updateTask]);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!data || submitting) return;

    setSubmitting(true);
    setBanner(null);
    const submissions = data.tasks
      .filter((task) => submissionsState[task.id]?.selected)
      .map((task) => {
        const state = submissionsState[task.id];
        const type = state.type as SubmissionType;
        return {
          taskId: task.id,
          type,
          url: type === 'document' ? undefined : state.url.trim(),
          uploadId: type === 'document' ? state.uploadId : undefined,
          note: state.note.trim(),
        };
      });

    try {
      const res = await applyToJob(data.job.id, { turnstileToken, submissions });
      setApplicationId(res.applicationId);
    } catch (err) {
      if (err instanceof ApiError) {
        switch (err.code) {
          case 'already_applied':
            setBanner({ kind: 'already_applied', message: err.message });
            break;
          case 'job_closed':
            setBanner({ kind: 'job_closed', message: err.message });
            break;
          case 'resume_required':
          case 'quota_exceeded':
          case 'invalid_submission':
          case 'validation_failed':
          case 'captcha_failed':
          default:
            toast.error(err.message);
        }
        // The server consumes the Turnstile token before other validation,
        // so ANY 4xx here has already burned it — not just captcha_failed.
        // Reusing it on the next submit would fail captcha_failed even
        // though the candidate never touched the widget, so force a fresh
        // challenge by remounting it (its `key` bump below) on every error.
        if (turnstileSiteKey) {
          setTurnstileToken(undefined);
          setTurnstileNonce((n) => n + 1);
        }
      } else {
        toast.error('Check your connection and try again.');
      }
    } finally {
      setSubmitting(false);
    }
  };

  if (loading) return <LoadingNotice />;
  if (loadError?.notFound) return <NotFoundNotice />;
  if (loadError) return <ErrorNotice message={loadError.message} onRetry={() => setLoadNonce((n) => n + 1)} />;
  if (applicationId) return <SuccessScreen />;
  if (!data) return null;
  if (!hasResume) return <NoResumeNotice jobTitle={data.job.title} />;
  if (data.job.status === 'closed') return <ClosedNotice />;

  const selectedCount = data.tasks.filter((task) => submissionsState[task.id]?.selected).length;
  const allComplete = data.tasks.every((task) => isTaskComplete(submissionsState[task.id] ?? EMPTY_TASK_SUBMISSION));
  const anyUploading = Object.values(uploading).some(Boolean);
  const turnstileBlocked = !!turnstileSiteKey && !turnstileToken;
  const canSubmit = selectedCount > 0 && allComplete && !anyUploading && !submitting && !turnstileBlocked;

  return (
    <div className="max-w-3xl mx-auto">
      <Link
        to={`/jobs/${data.job.id}`}
        className="velaigal-link text-sm font-medium inline-flex items-center gap-1.5"
      >
        <ArrowLeft className="h-3.5 w-3.5" strokeWidth={1.75} />
        Back to job
      </Link>

      <span className="label-editorial block mt-8 mb-3">Apply</span>
      <h1 className="font-display text-3xl font-medium text-ink tracking-tight">{data.job.title}</h1>
      <p className="mt-1 text-sm text-ink-tertiary">{data.job.companyName}</p>

      {banner && <div className="mt-8"><SubmitBanner kind={banner.kind} message={banner.message} /></div>}

      <form onSubmit={handleSubmit} className="mt-8">
        {data.tasks.length === 0 ? (
          <p className="text-sm text-ink-tertiary">This job doesn&apos;t have any tasks yet — check back soon.</p>
        ) : (
          <div className="space-y-6">
            {data.tasks.map((task, index) => (
              <TaskCard
                key={task.id}
                task={task}
                index={index}
                state={submissionsState[task.id] ?? EMPTY_TASK_SUBMISSION}
                uploading={!!uploading[task.id]}
                onToggle={() => toggleTask(task.id, task.allowedSubmissionTypes[0])}
                onChange={(patch) => updateTask(task.id, patch)}
                onFileSelect={(file) => void handleFileSelect(task.id, file)}
              />
            ))}
          </div>
        )}

        <TurnstileWidget
          key={turnstileNonce}
          onToken={(token) => {
            setTurnstileLoadFailed(false);
            setTurnstileToken(token);
          }}
          onError={() => setTurnstileLoadFailed(true)}
        />

        {turnstileLoadFailed && (
          <p role="alert" className="mt-4 text-xs text-error font-medium">
            Verification couldn&apos;t load — refresh and try again, or{' '}
            <button
              type="button"
              className="velaigal-link font-medium"
              onClick={() => {
                setTurnstileLoadFailed(false);
                setTurnstileNonce((n) => n + 1);
              }}
            >
              try again
            </button>
            .
          </p>
        )}

        <div className="mt-8 flex items-center gap-4">
          <button type="submit" className="btn-primary" disabled={!canSubmit}>
            {submitting ? (
              <>
                <span
                  className="inline-block h-3.5 w-3.5 rounded-full border-2 animate-spin"
                  style={{ borderColor: 'rgba(244, 240, 232, 0.35)', borderTopColor: 'var(--color-cream)' }}
                  aria-hidden="true"
                />
                <span>Sending…</span>
              </>
            ) : (
              'Send application'
            )}
          </button>
          {selectedCount === 0 && (
            <p className="text-xs text-ink-tertiary">Select at least one task to submit.</p>
          )}
          {turnstileBlocked && !turnstileLoadFailed && selectedCount > 0 && (
            <p className="text-xs text-ink-tertiary">Waiting on verification…</p>
          )}
        </div>
      </form>
    </div>
  );
};

export default Apply;
